// Account Documentation — everything we know about one sub-account.
//
// Tabs: Assets (with the link graph) · Risks · Profile (+ coverage) · Charter.
// The design rule throughout: never let a gap look like a clean result. Coverage is stated, an
// uncaptured definition says so, and an un-run collector is listed rather than omitted.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AccountDocumentation, AccountCharter, AssetDetail, RiskFinding, RiskSeverity, SAEntity,
} from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { formatApiTime } from '../../lib/time';
import { Button, Card, EmptyNote, ErrorNote, Spinner } from './ui';
import StepFlow, { type FlowStep } from './StepFlow';

const DASH = '—';

const TYPE_LABEL: Record<string, string> = {
  workflow: 'Workflows', form: 'Forms', survey: 'Surveys', funnel: 'Funnels', funnel_step: 'Funnel steps',
  website: 'Websites', custom_field: 'Custom fields', custom_value: 'Custom values',
  custom_object: 'Objects', pipeline: 'Pipelines', pipeline_stage: 'Stages', calendar: 'Calendars',
  calendar_group: 'Calendar groups', tag: 'Tags', trigger_link: 'Trigger links',
  email_template: 'Email templates', snippet: 'Snippets', membership_product: 'Membership',
  conversational_ai: 'Conversational AI', product: 'Products', user: 'Users',
  integration: 'Integrations', phone_number: 'Numbers', domain: 'Domains',
};
const typeLabel = (t: string) => TYPE_LABEL[t] ?? t.replace(/_/g, ' ');
const singular = (t: string) => {
  const l = typeLabel(t);
  return l.endsWith('s') ? l.slice(0, -1) : l;
};

const SEV_STYLE: Record<RiskSeverity, { chip: string; card: string; label: string }> = {
  critical: { chip: 'bg-rose-100 text-rose-700 ring-rose-200', card: 'border-l-rose-500', label: 'Critical' },
  high: { chip: 'bg-orange-100 text-orange-700 ring-orange-200', card: 'border-l-orange-500', label: 'High' },
  medium: { chip: 'bg-amber-100 text-amber-700 ring-amber-200', card: 'border-l-amber-400', label: 'Medium' },
  low: { chip: 'bg-slate-100 text-slate-600 ring-slate-200', card: 'border-l-slate-300', label: 'Low' },
  info: { chip: 'bg-sky-100 text-sky-700 ring-sky-200', card: 'border-l-sky-400', label: 'Info' },
};
const SEV_ORDER: RiskSeverity[] = ['critical', 'high', 'medium', 'low', 'info'];

const fmtDate = (iso: string | null): string => formatApiTime(iso, DASH);

type Tab = 'assets' | 'risks' | 'profile' | 'charter';

export default function AccountDoc({
  locationId, name, onBack,
}: { locationId: string; name: string; onBack: () => void }) {
  const [doc, setDoc] = useState<AccountDocumentation | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('assets');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDoc(await agencyApi.documentation(locationId));
      setError('');
    } catch (e) { setError((e as Error).message); }
  }, [locationId]);
  useEffect(() => { void load(); }, [load]);

  const rescan = async () => {
    setBusy(true);
    try { await agencyApi.runScan(locationId); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const scan = doc?.scan ?? null;
  const openRisks = (doc?.risks ?? []).filter((r) => r.status === 'open' || r.status === 'acknowledged');

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-0.5 rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100" aria-label="Back">←</button>
          <div>
            <h2 className="text-base font-semibold text-slate-900">{name}</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
              <span className="font-mono">{locationId}</span>
              <span>Documented {fmtDate(scan?.completed_at ?? null)}</span>
              {scan && (
                <button
                  onClick={() => setTab('profile')}
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    scan.coverage_ok === scan.coverage_total
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  }`}
                  title="What we could and could not read"
                >
                  {scan.coverage_ok}/{scan.coverage_total} sources read
                </button>
              )}
            </div>
          </div>
        </div>
        <Button onClick={rescan} disabled={busy}>{busy ? 'Documenting…' : 'Re-document'}</Button>
      </div>

      {error && <div className="mb-4"><ErrorNote error={error} /></div>}

      {!doc ? <Spinner label="Loading documentation…" /> : !scan ? (
        <EmptyNote>
          This sub-account has not been documented yet. <strong>Re-document</strong> reads its
          assets, links them together, and audits it for risk.
        </EmptyNote>
      ) : (
        <>
          <div className="mb-4 flex gap-1 border-b border-slate-200">
            {([
              ['assets', `Assets${doc.entities.length ? ` (${doc.entities.length})` : ''}`],
              ['risks', `Risks${openRisks.length ? ` (${openRisks.length})` : ''}`],
              ['profile', 'Profile'],
              ['charter', `Charter${doc.charter?.account_kind ? '' : ' •'}`],
            ] as Array<[Tab, string]>).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
                  tab === id ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'assets' && <AssetsTab doc={doc} onOpen={setSelected} />}
          {tab === 'risks' && <RisksTab doc={doc} onChanged={load} onOpenAsset={setSelected} />}
          {tab === 'profile' && <ProfileTab doc={doc} />}
          {tab === 'charter' && <CharterTab locationId={locationId} charter={doc.charter} onSaved={load} />}
        </>
      )}

      {selected && (
        <AssetDrawer
          entityPk={selected}
          entities={doc?.entities ?? []}
          onClose={() => setSelected(null)}
          onOpenAsset={setSelected}
        />
      )}
    </div>
  );
}

// ── Assets ─────────────────────────────────────────────────────────────────────────────────
function AssetsTab({ doc, onOpen }: { doc: AccountDocumentation; onOpen: (pk: string) => void }) {
  const [type, setType] = useState<string>('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const byPk = useMemo(() => new Map(doc.entities.map((e) => [e.id, e])), [doc.entities]);
  // Links per asset, both directions — an asset's inbound edges matter as much as its outbound
  // ones ("what would break if I deleted this?").
  const linksByPk = useMemo(() => {
    const m = new Map<string, Array<{ dir: 'out' | 'in'; relation: string; other: SAEntity | null; ref: string | null }>>();
    for (const e of doc.edges) {
      const out = m.get(e.from_pk) ?? [];
      out.push({ dir: 'out', relation: e.relation, other: e.to_pk ? byPk.get(e.to_pk) ?? null : null, ref: e.to_ref });
      m.set(e.from_pk, out);
      if (e.to_pk) {
        const inn = m.get(e.to_pk) ?? [];
        inn.push({ dir: 'in', relation: e.relation, other: byPk.get(e.from_pk) ?? null, ref: null });
        m.set(e.to_pk, inn);
      }
    }
    return m;
  }, [doc.edges, byPk]);

  const types = useMemo(() => Object.entries(doc.counts).sort((a, b) => b[1] - a[1]), [doc.counts]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return doc.entities.filter((e) => {
      if (type !== 'all' && e.entity_type !== type) return false;
      if (needle && !(e.name ?? '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [doc.entities, type, q]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => setType('all')}
          className={`rounded-full px-3 py-1 text-xs font-medium ${type === 'all' ? 'bg-brand text-brand-fg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        >
          All {doc.entities.length}
        </button>
        {types.map(([t, n]) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${type === t ? 'bg-brand text-brand-fg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {typeLabel(t)} {n}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter…"
          className="ml-auto w-48 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </div>

      {!rows.length ? (
        <EmptyNote>No assets of that kind.</EmptyNote>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-3 py-2.5 font-medium">Type</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium">Links</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const links = linksByPk.get(e.id) ?? [];
                const isOpen = expanded === e.id;
                return (
                  <>
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                      <td className="px-4 py-2.5">
                        <button onClick={() => onOpen(e.id)} className="text-left font-medium text-slate-900 hover:text-brand">
                          {e.name ?? <span className="text-slate-400">Untitled</span>}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">{singular(e.entity_type)}</td>
                      <td className="px-3 py-2.5">
                        {e.status ? (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            /publish|active|live/i.test(e.status) ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}>{e.status}</span>
                        ) : <span className="text-slate-300">{DASH}</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {links.length ? (
                          <button
                            onClick={() => setExpanded(isOpen ? null : e.id)}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 transition ${
                              isOpen ? 'bg-brand text-brand-fg ring-transparent' : 'bg-white text-slate-600 ring-slate-200 hover:ring-brand'
                            }`}
                          >
                            <span aria-hidden>⇄</span> {links.length}
                          </button>
                        ) : <span className="text-xs text-slate-300">none</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button variant="ghost" onClick={() => onOpen(e.id)}>Open</Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${e.id}-links`} className="border-b border-slate-100 bg-slate-50/60">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="space-y-1.5 pl-3">
                            {links.map((l, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="text-slate-400" aria-hidden>{l.dir === 'out' ? '→' : '←'}</span>
                                {l.other ? (
                                  <button onClick={() => onOpen(l.other!.id)} className="font-medium text-slate-700 hover:text-brand">
                                    {l.other.name ?? l.other.entity_id}
                                  </button>
                                ) : (
                                  <span className="font-mono text-xs text-slate-500">{l.ref}</span>
                                )}
                                <span className="text-xs text-slate-400">
                                  {l.other ? singular(l.other.entity_type) : 'not in this account'}
                                </span>
                                <span className="rounded bg-white px-1.5 py-px text-[11px] text-slate-500 ring-1 ring-slate-200">
                                  {l.relation.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ── Risks ──────────────────────────────────────────────────────────────────────────────────
function RisksTab({
  doc, onChanged, onOpenAsset,
}: { doc: AccountDocumentation; onChanged: () => void; onOpenAsset: (pk: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const risks = doc.risks;

  const act = async (r: RiskFinding, status: 'acknowledged' | 'muted' | 'open') => {
    setBusy(r.id);
    try { await agencyApi.setRiskStatus(r.id, status); onChanged(); }
    finally { setBusy(null); }
  };

  if (!risks.length) {
    return (
      <EmptyNote>
        No open findings. Note that this reflects the sources we could read — check{' '}
        <strong>Profile → coverage</strong> to see what was not collected.
      </EmptyNote>
    );
  }

  const grouped = SEV_ORDER.map((sev) => ({ sev, items: risks.filter((r) => r.severity === sev) })).filter((g) => g.items.length);

  return (
    <div className="space-y-5">
      {grouped.map(({ sev, items }) => (
        <div key={sev}>
          <div className="mb-2 flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${SEV_STYLE[sev].chip}`}>
              {SEV_STYLE[sev].label}
            </span>
            <span className="text-xs text-slate-400">{items.length}</span>
          </div>
          <div className="space-y-2">
            {items.map((r) => (
              <Card key={r.id} className={`border-l-4 p-4 ${SEV_STYLE[sev].card} ${r.status === 'acknowledged' ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold text-slate-900">{r.title}</h4>
                      <span className="rounded bg-slate-100 px-1.5 py-px text-[11px] text-slate-500">{r.category}</span>
                      {r.status === 'acknowledged' && (
                        <span className="rounded bg-slate-200 px-1.5 py-px text-[11px] text-slate-600">Acknowledged</span>
                      )}
                    </div>
                    {r.detail && <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.detail}</p>}
                    {r.fix && (
                      <p className="mt-2 flex gap-1.5 text-sm text-slate-700">
                        <span aria-hidden>→</span>
                        <span><span className="font-medium">Fix:</span> {r.fix}</span>
                      </p>
                    )}
                    {r.entity_pk && r.entity_name && (
                      <button onClick={() => onOpenAsset(r.entity_pk!)} className="mt-2 text-xs font-medium text-brand hover:underline">
                        View “{r.entity_name}”
                      </button>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {r.status === 'open' ? (
                      <>
                        <Button variant="ghost" onClick={() => act(r, 'acknowledged')} disabled={busy === r.id}>Acknowledge</Button>
                        <Button variant="ghost" onClick={() => act(r, 'muted')} disabled={busy === r.id}>Mute</Button>
                      </>
                    ) : (
                      <Button variant="ghost" onClick={() => act(r, 'open')} disabled={busy === r.id}>Reopen</Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Profile + coverage ─────────────────────────────────────────────────────────────────────
const DOMAIN_LABEL: Record<string, string> = {
  billing: 'Billing & plan', messaging: 'Messaging & compliance', email: 'Email deliverability',
  integrations: 'Integrations', users: 'Users & access', limits: 'Limits & usage',
  compliance: 'Compliance', config: 'Configuration', snapshot: 'Snapshot origin',
};

function FactValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-slate-300">{DASH}</span>;
  if (typeof value === 'boolean') {
    return <span className={`rounded px-1.5 py-px text-xs font-medium ${value ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{value ? 'Yes' : 'No'}</span>;
  }
  if (typeof value === 'string' || typeof value === 'number') return <span className="text-slate-700">{String(value)}</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-slate-300">none</span>;
    return <span className="text-slate-700">{value.length} item{value.length === 1 ? '' : 's'}</span>;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  return (
    <div className="space-y-0.5">
      {entries.slice(0, 12).map(([k, v]) => (
        <div key={k} className="flex gap-2 text-xs">
          <span className="w-40 shrink-0 truncate text-slate-400">{k}</span>
          <span className="text-slate-700">
            {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v === null ? DASH : typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v)}
          </span>
        </div>
      ))}
      {entries.length > 12 && <div className="text-xs text-slate-400">+{entries.length - 12} more</div>}
    </div>
  );
}

const STATUS_ICON: Record<string, { icon: string; cls: string }> = {
  ok: { icon: '✓', cls: 'text-emerald-600' },
  unavailable: { icon: '–', cls: 'text-amber-600' },
  forbidden: { icon: '⚠', cls: 'text-orange-600' },
  error: { icon: '×', cls: 'text-rose-600' },
  skipped: { icon: '·', cls: 'text-slate-400' },
};

function ProfileTab({ doc }: { doc: AccountDocumentation }) {
  const [catalog, setCatalog] = useState<Array<{ id: string; label: string; lane: string; verified: boolean; note: string | null }> | null>(null);
  useEffect(() => { agencyApi.collectors().then(setCatalog).catch(() => setCatalog([])); }, []);

  const byDomain = useMemo(() => {
    const m = new Map<string, typeof doc.facts>();
    for (const f of doc.facts) {
      const arr = m.get(f.domain) ?? [];
      arr.push(f);
      m.set(f.domain, arr);
    }
    return [...m.entries()];
  }, [doc.facts]);

  const items = doc.scan?.items ?? [];
  const ran = new Set(items.map((i) => i.collector));
  const missing = (catalog ?? []).filter((c) => !ran.has(c.id));

  return (
    <div className="space-y-5">
      {/* Coverage first, deliberately: what we know is only meaningful next to what we could not read. */}
      <Card className="p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Coverage</h3>
          <span className="text-xs text-slate-500">
            {doc.scan?.coverage_ok ?? 0} of {doc.scan?.coverage_total ?? 0} sources returned data
          </span>
        </div>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {items.map((i) => {
            const s = STATUS_ICON[i.status] ?? STATUS_ICON.skipped;
            return (
              <div key={i.id} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 w-4 shrink-0 text-center font-semibold ${s.cls}`} aria-hidden>{s.icon}</span>
                <div className="min-w-0 flex-1">
                  <span className="text-slate-700">{i.label ?? i.collector}</span>
                  {i.status === 'ok' ? (
                    <span className="ml-1.5 text-xs text-slate-400">{i.stored} stored</span>
                  ) : (
                    <div className="text-xs text-slate-500">{i.reason ?? i.status}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!!missing.length && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="mb-1.5 text-xs font-medium text-slate-600">Not collected yet</p>
            <div className="grid gap-1 sm:grid-cols-2">
              {missing.map((c) => (
                <div key={c.id} className="text-xs text-slate-500">
                  <span className="text-slate-400" aria-hidden>○</span> {c.label}
                  {c.lane === 'app' && <span className="ml-1 text-slate-400">· needs an in-app run</span>}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              These read surfaces the marketplace token cannot reach. Until they run, risk checks
              that depend on them stay silent rather than guessing.
            </p>
          </div>
        )}
      </Card>

      {!byDomain.length ? (
        <EmptyNote>No account profile facts captured yet.</EmptyNote>
      ) : (
        byDomain.map(([domain, facts]) => (
          <Card key={domain} className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">{DOMAIN_LABEL[domain] ?? domain}</h3>
            <div className="space-y-2.5">
              {facts.map((f) => (
                <div key={f.id} className="grid gap-1 sm:grid-cols-[180px_1fr]">
                  <div className="text-sm text-slate-500">{f.label ?? f.key}</div>
                  <div className="text-sm"><FactValue value={f.value} /></div>
                </div>
              ))}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

// ── Charter ────────────────────────────────────────────────────────────────────────────────
// Deliberately the same three questions (and option values) PatchyHub asks after an import, so
// the answers can be handed to them verbatim. `version_management: yes` also marks a snapshot
// master for our own fleet view.
const KINDS: Array<[string, string]> = [
  ['agency', 'Agency account'], ['business', 'Business account'], ['snapshot', 'Snapshot'],
  ['demo', 'Demo or training account'], ['other', 'Other'],
];
const AUDIENCES: Array<[string, string]> = [
  ['prospective_clients', 'Prospective clients'], ['current_or_former_clients', 'Current or former clients'],
  ['internal_team', 'Internal team'], ['other', 'Other'],
];

function CharterTab({
  locationId, charter, onSaved,
}: { locationId: string; charter: AccountCharter | null; onSaved: () => void }) {
  const [kind, setKind] = useState(charter?.account_kind ?? '');
  const [vm, setVm] = useState(charter?.version_management ?? '');
  const [aud, setAud] = useState<string[]>(charter?.audiences ?? []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await agencyApi.saveCharter(locationId, {
        account_kind: (kind || null) as AccountCharter['account_kind'],
        version_management: (vm || null) as AccountCharter['version_management'],
        audiences: aud as AccountCharter['audiences'],
      });
      setSaved(true);
      onSaved();
    } finally { setSaving(false); }
  };

  const Row = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition ${
        active ? 'border-brand bg-brand/5 text-slate-900' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
      }`}
    >
      <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${active ? 'border-brand' : 'border-slate-300'}`}>
        {active && <span className="h-2 w-2 rounded-full bg-brand" />}
      </span>
      {children}
    </button>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-slate-500">
        A few things only a human knows about this account. They shape how we document it — and they
        are the same questions PatchyHub asks, so the answers travel if that integration lands.
      </p>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">What kind of account does this represent?</h3>
        <div className="space-y-1.5">
          {KINDS.map(([v, label]) => (
            <Row key={v} active={kind === v} onClick={() => { setKind(v); setSaved(false); }}>{label}</Row>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Do you need version management?</h3>
        <div className="space-y-1.5">
          <Row active={vm === 'yes'} onClick={() => { setVm('yes'); setSaved(false); }}>
            Yes — I manage how updates are pushed out to a snapshot.
          </Row>
          <Row active={vm === 'no'} onClick={() => { setVm('no'); setSaved(false); }}>
            No — this is a live client or agency account where changes go live immediately.
          </Row>
        </div>
        {vm === 'yes' && (
          <p className="mt-2 text-xs text-slate-500">
            We will treat this account as a snapshot master: the Snapshots view can baseline it and
            measure how far each deployment has drifted.
          </p>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Who does the agency communicate with through this account?</h3>
        <div className="space-y-1.5">
          {AUDIENCES.map(([v, label]) => {
            const on = aud.includes(v);
            return (
              <button
                key={v}
                onClick={() => { setAud(on ? aud.filter((a) => a !== v) : [...aud, v]); setSaved(false); }}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition ${
                  on ? 'border-brand bg-brand/5' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border-2 ${on ? 'border-brand bg-brand text-brand-fg' : 'border-slate-300'}`}>
                  {on && <span className="text-[10px] leading-none">✓</span>}
                </span>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving || !kind}>{saving ? 'Saving…' : 'Save charter'}</Button>
        {saved && <span className="text-sm text-emerald-600">Saved</span>}
        {charter?.answered_at && !saved && (
          <span className="text-xs text-slate-400">Last answered {fmtDate(charter.answered_at)}</span>
        )}
      </div>
    </div>
  );
}

// ── Asset detail drawer ────────────────────────────────────────────────────────────────────
function AssetDrawer({
  entityPk, entities, onClose, onOpenAsset,
}: {
  entityPk: string; entities: SAEntity[];
  onClose: () => void; onOpenAsset: (pk: string) => void;
}) {
  const [detail, setDetail] = useState<(AssetDetail & { definition_available: boolean }) | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<'diagram' | 'list'>('diagram');

  useEffect(() => {
    setDetail(null);
    setError('');
    agencyApi.assetDetail(entityPk).then(setDetail).catch((e) => setError((e as Error).message));
  }, [entityPk]);

  // A link chip on a step names a target by GHL id; resolve it to something clickable when the
  // asset is in this account.
  const byEntityId = useMemo(() => new Map(entities.map((e) => [e.entity_id, e])), [entities]);
  const openByRef = (_type: string, ref: string) => {
    const hit = byEntityId.get(ref);
    if (hit) onOpenAsset(hit.id);
  };

  const steps = (detail?.steps ?? []) as unknown as FlowStep[];
  const triggers = (detail?.triggers ?? []) as unknown as FlowStep[];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/20" onClick={onClose} aria-hidden />
      <aside className="relative flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900">
              {detail?.entity.name ?? 'Asset'}
            </h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              <span>{detail ? singular(detail.entity.entity_type) : ''}</span>
              {detail?.entity.status && <span>· {detail.entity.status}</span>}
              {detail?.origin?.origin_type === 'snapshot' && (
                <span className="rounded bg-indigo-50 px-1.5 py-px text-[11px] text-indigo-700">From snapshot</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-slate-400 hover:bg-slate-100" aria-label="Close">×</button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && <ErrorNote error={error} />}
          {!detail && !error && <Spinner label="Loading asset…" />}

          {detail && (
            <div className="space-y-5">
              {!!detail.risks.length && (
                <div className="space-y-2">
                  {detail.risks.map((r) => (
                    <div key={r.id} className={`rounded-lg border-l-4 bg-slate-50 p-3 ${SEV_STYLE[r.severity].card}`}>
                      <div className="text-sm font-medium text-slate-900">{r.title}</div>
                      {r.fix && <div className="mt-0.5 text-xs text-slate-600">→ {r.fix}</div>}
                    </div>
                  ))}
                </div>
              )}

              {!!detail.fields.length && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Details</h4>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {detail.fields.map((f, i) => (
                      <div key={i} className="flex gap-2 text-sm">
                        <span className="w-32 shrink-0 text-slate-400">{f.label}</span>
                        <span className="truncate text-slate-700">{f.value ?? DASH}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Flow {steps.length > 0 && <span className="text-slate-400">· {steps.length} steps</span>}
                  </h4>
                  {steps.length > 0 && (
                    <div className="flex gap-1">
                      {(['diagram', 'list'] as const).map((v) => (
                        <button
                          key={v}
                          onClick={() => setView(v)}
                          className={`rounded-md px-2 py-0.5 text-xs font-medium capitalize ${
                            view === v ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:bg-slate-100'
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {!detail.definition_available ? (
                  // Honesty again: "no steps" and "we never captured the definition" are
                  // different facts and must not look the same.
                  <p className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                    The full definition was not captured for this asset, so its steps are unknown.
                    Re-document the account to try again.
                  </p>
                ) : view === 'diagram' ? (
                  <StepFlow
                    triggers={triggers}
                    steps={steps}
                    onOpenLink={openByRef}
                    resolveName={(ref) => byEntityId.get(ref)?.name ?? null}
                  />
                ) : (
                  <ol className="space-y-1.5">
                    {triggers.map((t) => (
                      <li key={t.id} className="flex gap-3 rounded-lg border border-brand/30 bg-brand/5 p-2.5 text-sm">
                        <span className="text-xs font-semibold uppercase text-brand">When</span>
                        <div>
                          <div className="font-medium text-slate-900">{t.name}</div>
                          {t.summary && <div className="text-xs text-slate-600">{t.summary}</div>}
                        </div>
                      </li>
                    ))}
                    {steps.map((s, i) => (
                      <li key={s.id} className="flex gap-3 rounded-lg border border-slate-200 p-2.5 text-sm">
                        <span className="w-5 shrink-0 text-right text-xs text-slate-400">{i + 1}</span>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900">{s.name ?? s.type_label ?? s.type}</div>
                          <div className="text-xs text-slate-500">{s.type_label ?? s.type}</div>
                          {s.summary && <div className="mt-0.5 text-xs text-slate-600">{s.summary}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {!!detail.links.length && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Connections · {detail.links.length}
                  </h4>
                  <div className="space-y-1">
                    {detail.links.map((l, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                        <span className="text-slate-400" aria-hidden>{l.direction === 'out' ? '→' : '←'}</span>
                        {l.entity_pk ? (
                          <button onClick={() => onOpenAsset(l.entity_pk!)} className="font-medium text-slate-700 hover:text-brand">
                            {l.name ?? l.entity_pk}
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-slate-500">{l.ref}</span>
                        )}
                        <span className="ml-auto rounded bg-slate-100 px-1.5 py-px text-[11px] text-slate-500">
                          {l.relation.replace(/_/g, ' ')}
                        </span>
                        {!l.resolved && (
                          <span className="rounded bg-rose-50 px-1.5 py-px text-[11px] text-rose-600">missing</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
